<template>
  <responsive-drawer-layout>
    <!-- Page content -->
    <template v-slot:content>
      <div v-if="isLoaded" class="mx-0 inherit-height scroll-container">
        <v-card class="cardContainer mx-auto mt-3 pb-2 mb-6" style="max-width: 800px" v-if="employee">
          <div class="top background">
            <div style="display: flex">
              <div class="person">
                <div>{{ person.title }}</div>
                <h2>{{ person.firstname }} {{ person.lastname }}</h2>
                <h3>{{ person.department }}</h3>
                <h4>{{ person.position }}</h4>
              </div>
              <div class="employee-avatar">
                <v-avatar size="140">
                  <img v-if="fileUrl" :src="fileUrl" alt="Bild" class="primary" />
                  <v-avatar size="140" color="primary" v-else>
                    <span class="white--text text-h5">{{ getInitials }}</span>
                  </v-avatar>
                </v-avatar>
              </div>
            </div>
            <div class="mt-4 flex-row">
              <v-chip dark color="accent" class="tags ml-0" v-for="(tag, ind) in tags" :key="ind">
                {{ tag }}
              </v-chip>
            </div>
          </div>

          <v-divider></v-divider>
          <div class="pt-5 px-4 px-sm-4">
            <div v-if="employee.metadata && employee.metadata.email">
              <h3 class="header3">{{ mailLabel }}</h3>
              <div class="border">
                <div @click="goToMail(mail.value)" v-for="(mail, index) in emails" :key="index">
                  <div class="grey--text">{{ mail.label }}</div>
                  <div class="subheading pb-2 pl-4">{{ mail.value }}</div>
                </div>
              </div>
            </div>
            <br />

            <div v-if="phones.length > 0">
              <h3 class="header3">{{ phoneLabel }}</h3>
              <div class="border tel">
                <div @click="goToTelephone(tel.value)" v-for="(tel, ind) in phones" :key="ind">
                  <div class="grey--text">{{ tel.label }}</div>
                  <div class="subheading pb-2 pl-4">{{ tel.value }}</div>
                </div>
              </div>
              <br />
            </div>
            <div class="pb-4" v-if="employee.metadata && employee.metadata.address">
              <h3 class="header3">{{ addressLabel }}</h3>
              <div v-for="(ad, ind) in addresses" :key="ind" class="border ml-1">
                <div class="grey--text" v-if="ad.label !== $t('addr')">{{ ad.label }}</div>
                <div class="subheading pb-2 pl-4">{{ ad.street }}<br />{{ ad.zip }} {{ ad.city }}<br />{{ ad.country }}</div>
              </div>
            </div>
            <div>
              <h3 v-if="bosses.length > 0" class="header3">{{ supervisorLabel }}</h3>
              <div class="border ml-1" v-for="(boss, ind) in bosses" :key="ind">
                {{ boss.firstname }}
                {{ boss.lastname }}
              </div>
              <h3 v-if="subordinates.length > 0" class="header3">{{ teamLabel }}</h3>
              <div class="border ml-1" v-for="(sub, ind) in subordinates" :key="'sub' + ind">
                <template v-if="sub">
                  {{ sub.firstname }}
                  {{ sub.lastname }}
                </template>
              </div>

              <h3 class="header3" v-if="representatives">{{ representativeLabel }}</h3>
              <div class="border ml-1" v-for="(representative, ind) in representatives" :key="ind">
                {{ representative.firstname }}
                {{ representative.lastname }}
              </div>
            </div>
          </div>
        </v-card>
      </div>

      <!-- loading display -->
      <info-card v-if="!isLoaded" :textRows="[$t('isLoading.moduleIsGettingLoaded')]">
        <template v-slot:title>{{ $t('isLoading.title') }}</template>
      </info-card>
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import InfoCard from '@/components/container/InfoCard';
import index from './index';
import employeeService from '@/modules/employee/employeeService';

export default {
  name: 'Employee',
  mixins: [common, configMixin, dateTimeMixin],

  components: {
    ResponsiveDrawerLayout,
    InfoCard,
  },

  computed: {
    ...mapState('employee', [
      'personList',
      'isLoaded',
      'personItemById',
      'person',
      'photoByEmployeeId',
      'personById',
      'metadataByLabel',
      'section',
      'personItemById',
      'personItemByUuid',
    ]),

    phones() {
      return this.employee.metadata && this.employee.metadata.phone ? this.employee.metadata.phone : [];
    },
    bosses() {
      return this.person.boss
        ? Object.keys(this.person.boss).length > 0 &&
            Object.keys(this.person.boss)
              .map((k) => this.person.boss[k])
              .filter((i) => !!i)
              .sort((a, b) => a.lastname && a.lastname.localeCompare(b.lastname))
        : [];
    },

    representatives() {
      return (
        this.person.representative &&
        Object.keys(this.person.representative).length > 0 &&
        Object.keys(this.person.representative)
          .map((k) => this.person.representative[k])
          .filter((i) => !!i)
          .sort((a, b) => a.lastname && a.lastname.localeCompare(b.lastname))
      );
    },
    subordinates() {
      console.debug('person.subordinate', this.person.subordinate);

      return this.person.subordinate
        ? Object.keys(this.person.subordinate).length > 0 &&
            Object.keys(this.person.subordinate)
              .map((k) => this.person.subordinate[k])
              .filter((i) => !!i)
              .sort((a, b) => a.lastname && a.lastname.localeCompare(b.lastname))
        : [];
    },
    mailLabel() {
      return this.getSpecificLabel('email');
    },

    phoneLabel() {
      return this.getSpecificLabel('phone');
    },

    addressLabel() {
      return this.getSpecificLabel('address');
    },
    representativeLabel() {
      return this.getSpecificLabel('representative');
    },
    teamLabel() {
      return this.getSpecificLabel('subordinate');
    },
    supervisorLabel() {
      return this.getSpecificLabel('supervisor');
    },

    fileUrl() {
      let fileName = this.getPhotoFilename(this.employee);
      let fileUrl;
      if (fileName) {
        fileUrl = this.moduleFileUrl(index.moduleId, fileName);
      }
      return fileUrl;
    },

    employee() {
      let id = Number(this.$route.params.employeeId);
      return this.personById[id];
    },
    person() {
      return this.personItemById[Number(this.$route.params.employeeId)];
    },
    addresses() {
      return this.person ? this.person.address : null;
    },
    tags() {
      return this.person ? this.person.tags : null;
    },
    emails() {
      return this.person ? this.person.email : null;
    },

    /**
     * This Function extracts the initials from the firstname and lastname and returns them
     * @returns {string}
     */
    getInitials() {
      return employeeService.getInitials(this.person);
    },
  },
  methods: {
    findItemWithLabel(label) {
      return this.section.find((item) => item.label === label);
    },

    getSpecificLabel(label) {
      const lang = localStorage.getItem('lang');
      let section = this.findItemWithLabel(label);

      if (section && section.extended && section.extended.i18n && section.extended.i18n.label) {
        return section.extended.i18n.label[lang];
      }

      switch (label) {
        case 'email':
          return this.$t('mail');
        case 'phone':
          return this.$t('tel');
        case 'address':
          return this.$t('addr');
        default:
          return '';
      }
    },

    getPhotoFilename(employee) {
      let employeePhoto = this.photoByEmployeeId[employee.id];
      console.debug('employeePhoto', employeePhoto);
      return employeePhoto ? employeePhoto.filename : null;
    },

    goToTelephone(value) {
      window.location.href = 'tel:' + value;
    },

    goToMail(value) {
      window.location.href = 'mailto:' + value;
    },
  },
};
</script>
<style>
.theme--light .cardContainer {
  background: #f6f6f6 !important;
}

.theme--dark .cardContainer {
  background: #202020;
}

.top {
  display: flex;
  flex-direction: column;
  padding: 20px;
}

.person {
  flex: 1;
}

.employee-avatar {
  display: flex;
  align-items: center;
}

.tags {
  display: inline;
  flex-wrap: wrap;
  margin: 4px;
}

.header3 {
  margin-left: 4px;
  margin-top: 12px;
}

.border {
  gap: 10px 20px;
  padding: 5px;
  border-radius: 6px;
  margin: 5px;
}

.theme--light .border {
  border: 1px solid #e0e0e0;
  background: #fafafa;
}

.theme--dark .border {
  border: 1px solid #303030;
  background: #252525;
}
</style>
